import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTransactionRatioPaymentRevenueThunk, fetchTransactionSummaryByMonthThunk, fetchTransactionSummaryByTypeThunk, fetchTransactionSummarySupplierThunk, fetchTransactionSummaryThunk } from '@/modules/finance/redux/TransactionRedux';
import FinancialOverviewRow1 from './FinancialOverviewRow1';
import FinancialOverviewRow2 from './FinancialOverviewRow2';
import FinancialOverviewRow3 from './FinancialOverviewRow3';
import FinancialOverviewRow4 from './FinancialOverviewRow4';

interface FinanceOverviewProps {
  selectedPeriod: Date | null;
  companyId: string;
  onSummaryCardClick?: (payload: { key: string; title: string; type: string; month: string; companyId: string }) => void;
  onRow2CellClick?: (payload: { month: string; type: 'receipt' | 'payment'; companyId: string }) => void;
  onRow3CellClick?: (payload: { month: string; transactionTypeId: string; transactionTypeName: string; companyId: string }) => void;
  onSupplierDebtClick?: (payload: { supplierId: string; supplierName: string; month: string; companyId: string }) => void;
}

const toMonthParam = (value: Date | null): string => {
  if (!value) {
    return '';
  }

  return `${value.getMonth() + 1}-${value.getFullYear()}`;
};

const toMonthDisplayLabel = (value: Date | null): string => {
  if (!value) {
    return 'Đến tháng';
  }

  return `Đến tháng ${value.getMonth() + 1}/${value.getFullYear()}`;
};

const FinanceOverview: React.FC<FinanceOverviewProps> = ({ selectedPeriod, companyId, onSummaryCardClick, onRow2CellClick, onRow3CellClick, onSupplierDebtClick }) => {
  const dispatch = useDispatch<any>();
  const summaryReport = useSelector((state: any) => state.transactions?.summaryReport);
  const monthlySummaryReport = useSelector((state: any) => state.transactions?.monthlySummaryReport || []);
  const supplierSummaryReport = useSelector((state: any) => state.transactions?.supplierSummaryReport || []);
  const byTypeSummaryReport = useSelector((state: any) => state.transactions?.byTypeSummaryReport || { types: [], monthlyRows: [] });
  const ratioSummaryReport = useSelector((state: any) => state.transactions?.ratioSummaryReport || { types: [], monthlyRows: [] });

  const monthParam = React.useMemo(() => toMonthParam(selectedPeriod), [selectedPeriod]);
  const periodLabel = React.useMemo(() => toMonthDisplayLabel(selectedPeriod), [selectedPeriod]);
  const row1TypeMap = React.useMemo(() => ({
    income: 'receipt',
    expense: 'payment',
    'supplier-debt': 'supplier_debt',
    'net-fund': 'net_fund',
    'fund-balance': 'fund_balance',
  } as Record<string, string>), []);

  React.useEffect(() => {
    if (!monthParam || !companyId) {
      return;
    }

    dispatch(fetchTransactionSummaryThunk({ month: monthParam, company: companyId }));
    dispatch(fetchTransactionSummaryByMonthThunk({ month: monthParam, company: companyId }));
    dispatch(fetchTransactionSummarySupplierThunk({ month: monthParam, company: companyId }));
    dispatch(fetchTransactionSummaryByTypeThunk({ month: monthParam, company: companyId }));
    dispatch(fetchTransactionRatioPaymentRevenueThunk({ month: monthParam, company: companyId }));
  }, [companyId, dispatch, monthParam]);

  const totalIncome = Number(summaryReport?.totalIncome || 0);
  const totalExpense = Number(summaryReport?.totalExpense || 0);
  const fundBalance = Number(summaryReport?.fundBalance ?? (totalIncome - totalExpense));
  const supplierDebt = Number(summaryReport?.supplierDebt || 0);
  const netFundAfterDebt = Number(summaryReport?.netFundAfterDebt ?? (fundBalance - supplierDebt));
  const monthlySummary = Array.isArray(monthlySummaryReport) ? monthlySummaryReport : [];
  const supplierDebtDetails = (Array.isArray(supplierSummaryReport) ? supplierSummaryReport : []).map((s: any) => ({
    supplierId: String(s.supplierId || s.supplier_id || s.id || ''),
    supplierName: s.supplierName || s.supplier_name || '',
    debtAmount: Number(s.debtAmount ?? s.supplier_debt ?? 0),
  }));
  const transactionTypeCatalog: Array<{ id: string; name: string; category: string; code: string }> = byTypeSummaryReport.types.map((t: any) => ({
    id: t.id,
    name: t.name,
    category: t.category === 'receipt' ? 'Thu' : 'Chi',
    code: String(t.code || ''),
  }));
  const incomeTypeCount = transactionTypeCatalog.filter((item) => item.category === 'Thu').length;
  const expenseTypeCount = transactionTypeCatalog.filter((item) => item.category === 'Chi').length;
  const monthlyCategoryMatrix: Array<{ month: string; values: number[]; totalReceipt: number; totalPayment: number; profit: number; profitPercentage: number }> = byTypeSummaryReport.monthlyRows.map((row: any) => ({
    month: row.month,
    values: byTypeSummaryReport.types.map((t: any) => Number(row.values?.[t.id] || 0)),
    totalReceipt: Number(row.totalReceipt || 0),
    totalPayment: Number(row.totalPayment || 0),
    profit: Number(row.profit || 0),
    profitPercentage: Number(row.profitPercentage || 0),
  }));

  const formatVnd = (value: number) => `${value.toLocaleString('vi-VN')}`;
  const formatTableAmount = (value: unknown) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue === 0) {
      return '-';
    }

    return formatVnd(numericValue);
  };

  const row3Columns = React.useMemo(() => {
    const monthColumn = {
      id: 'month',
      label: 'Tháng',
      width: '40px',
      headerAlign: 'center' as const,
      borderRight: true,
      render: (value: unknown, row: any) => (
        <Typography variant='body2' sx={{ fontWeight: row?.isTotal ? 700 : 500 }}>
          {String(value || '-')}
        </Typography>
      ),
    };

    const dynamicColumns = transactionTypeCatalog.map((item, index) => ({
      id: `type_${index}`,
      label: <span style={{ whiteSpace: 'nowrap' }}>{item.name}</span>,
      width: '60px',
      headerAlign: 'center' as const,
      align: 'right' as const,
      render: (value: unknown, row: any) => (
        <Typography
          variant='body2'
          onClick={() => {
            if (onRow3CellClick && item.id && row?.month && !row?.isTotal) {
              onRow3CellClick({ month: row.month, transactionTypeId: item.id, transactionTypeName: item.name, companyId });
            }
          }}
          sx={{
            fontWeight: row?.isTotal ? 700 : 400,
            ...(onRow3CellClick && item.id && !row?.isTotal ? { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : {}),
          }}
        >
          {formatTableAmount(value)}
        </Typography>
      ),
    }));

    const totalReceiptColumn = {
      id: 'total_receipt',
      label: <span style={{ whiteSpace: 'nowrap' }}>Tổng thu</span>,
      width: '150px',
      headerAlign: 'center' as const,
      align: 'right' as const,
      render: (value: unknown, row: any) => (
        <Typography variant='body2' sx={{ fontWeight: row?.isTotal ? 700 : 600 }}>
          {formatTableAmount(value)}
        </Typography>
      ),
    };
    const totalPaymentColumn = {
      id: 'total_payment',
      label: <span style={{ whiteSpace: 'nowrap' }}>Tổng chi</span>,
      width: '150px',
      headerAlign: 'center' as const,
      align: 'right' as const,
      render: (value: unknown, row: any) => (
        <Typography variant='body2' sx={{ fontWeight: row?.isTotal ? 700 : 600 }}>
          {formatTableAmount(value)}
        </Typography>
      ),
    };
    const profitColumn = {
      id: 'profit',
      label: <span style={{ whiteSpace: 'nowrap' }}>Lợi nhuận</span>,
      width: '150px',
      headerAlign: 'center' as const,
      align: 'right' as const,
      render: (value: unknown, row: any) => {
        const n = Number(value);
        return (
          <Typography variant='body2' sx={{ fontWeight: row?.isTotal ? 700 : 600 }}>
            {Number.isFinite(n) && n !== 0 ? formatVnd(n) : '-'}
          </Typography>
        );
      },
    };

    const profitRateColumn = {
      id: 'profit_rate',
      label: <span style={{ whiteSpace: 'nowrap' }}>% Lợi nhuận</span>,
      width: '120px',
      headerAlign: 'center' as const,
      align: 'right' as const,
      render: (value: unknown, row: any) => {
        const rate = Number(value);
        return (
          <Typography variant='body2' sx={{ fontWeight: row?.isTotal ? 700 : 600 }}>
            {Number.isFinite(rate) && rate !== 0 ? `${rate.toFixed(2)}%` : '-'}
          </Typography>
        );
      },
    };

    return [monthColumn, ...dynamicColumns, totalReceiptColumn, totalPaymentColumn, profitColumn, profitRateColumn];
  }, [companyId, onRow3CellClick, transactionTypeCatalog]);

  const row3Data = React.useMemo(() => {
    const monthlyRows = monthlyCategoryMatrix.map((matrixRow) => {
      const rowData: Record<string, string | number> = {
        id: `month-${matrixRow.month}`,
        month: matrixRow.month,
      };

      matrixRow.values.forEach((value, valueIndex) => {
        rowData[`type_${valueIndex}`] = value;
      });
      rowData['total_receipt'] = matrixRow.totalReceipt;
      rowData['total_payment'] = matrixRow.totalPayment;
      rowData['profit'] = matrixRow.profit;
      rowData['profit_rate'] = matrixRow.profitPercentage;
      return rowData;
    });
    return monthlyRows;
  }, [monthlyCategoryMatrix]);

  const row3GroupHeaderRows = React.useMemo(
    () => [[
      { label: '', colSpan: 1, align: 'left' as const },
      { label: 'Thu', colSpan: Math.max(incomeTypeCount, 1), align: 'center' as const },
      { label: 'Chi', colSpan: Math.max(expenseTypeCount, 1), align: 'center' as const },
      { label: 'Tổng hợp', colSpan: 4, align: 'center' as const },
    ]],
    [incomeTypeCount, expenseTypeCount]
  );

  const formatRatioPercent = (value: number | null | undefined) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return '-';
    }

    if (Number(value) === 0) {
      return '-';
    }

    return `${(Number(value) * 100).toFixed(2)}%`;
  };

  const row4TransactionTypeCatalog: Array<{ id: string; name: string; code: string }> = ratioSummaryReport.types.map((t: any) => ({
    id: String(t.id || ''),
    name: String(t.name || ''),
    code: String(t.code || t.transaction_type_code || t.id || ''),
  }));

  const row4MonthlyMatrix: Array<{ month: string; values: Array<number | null>; totalCostRevenueRatio: number | null; profitPercentage: number | null; totalRatio: number | null }> = ratioSummaryReport.monthlyRows.map((row: any) => ({
    month: String(row.month || ''),
    values: row4TransactionTypeCatalog.map((t) => {
      const rawValue = row.values?.[t.id];
      return rawValue === null || rawValue === undefined ? null : Number(rawValue);
    }),
    totalCostRevenueRatio: row.totalCostRevenueRatio,
    profitPercentage: row.profitPercentage,
    totalRatio: row.totalRatio,
  }));

  const row4Columns = React.useMemo(() => {
    const monthColumn = {
      id: 'month',
      label: 'Tháng',
      width: '40px',
      borderRight: true,
      render: (value: unknown) => (
        <Typography variant='body2'>
          {String(value || '-')}
        </Typography>
      ),
    };

    const dynamicColumns = row4TransactionTypeCatalog.map((item, index) => ({
      id: `type_${index}`,
      label: <span style={{ whiteSpace: 'nowrap' }}>{item.name}</span>,
      width: 'auto',
      headerAlign: 'center' as const,
      align: 'right' as const,
      render: (value: unknown) => (
        <Typography
          variant='body2'
        >
          {formatRatioPercent(value as number | null | undefined)}
        </Typography>
      ),
    }));

    const totalCostRatioColumn = {
      id: 'total_cost_revenue_ratio',
      label: <span style={{ whiteSpace: 'nowrap' }}>Tổng % Chi phí</span>,
      width: '140px',
      headerAlign: 'center' as const,
      align: 'right' as const,
      borderRight: true,
      render: (value: unknown) => (
        <Typography variant='body2'>
          {value === null || value === undefined || Number(value) === 0 ? '-' : `${Number(value).toFixed(2)}%`}
        </Typography>
      ),
    };

    const profitRatioColumn = {
      id: 'profit_percentage',
      label: <span style={{ whiteSpace: 'nowrap' }}>% Lợi nhuận</span>,
      width: '100px',
      headerAlign: 'center' as const,
      align: 'right' as const,
      render: (value: unknown) => (
        <Typography variant='body2'>
          {value === null || value === undefined || Number(value) === 0 ? '-' : `${Number(value).toFixed(2)}%`}
        </Typography>
      ),
    };

    return [monthColumn, ...dynamicColumns, totalCostRatioColumn, profitRatioColumn];
  }, [row4TransactionTypeCatalog]);

  const row4Data = React.useMemo(() => {
    return row4MonthlyMatrix.map((matrixRow) => {
      const rowData: Record<string, string | number | null> = {
        id: `ratio-${matrixRow.month}`,
        month: matrixRow.month,
      };

      matrixRow.values.forEach((value, valueIndex) => {
        rowData[`type_${valueIndex}`] = value;
      });
      rowData['total_cost_revenue_ratio'] = matrixRow.totalCostRevenueRatio;
      rowData['profit_percentage'] = matrixRow.profitPercentage;
      return rowData;
    });
  }, [row4MonthlyMatrix]);

  const row4GroupHeaderRows: Array<Array<{ label: string; colSpan: number; align?: 'left' | 'center' | 'right' }>> = [];

  return (
    <>

    <Box
      sx={{
        p: 0,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
    >
      {/* Top Bar */}
      
      <Grid container spacing={3} sx={{ width: '100%', m: 0, minWidth: 0 }}>
        <FinancialOverviewRow1
          totalIncome={totalIncome}
          totalExpense={totalExpense}
          fundBalance={fundBalance}
          supplierDebt={supplierDebt}
          netFundAfterDebt={netFundAfterDebt}
          periodLabel={periodLabel}
          formatVnd={formatVnd}
          onCardClick={(payload) => {
            if (!monthParam || !companyId) {
              return;
            }

            const mappedType = row1TypeMap[payload.cardKey] || payload.cardKey;
            onSummaryCardClick?.({
              key: payload.cardKey,
              title: payload.title,
              type: mappedType,
              month: monthParam,
              companyId,
            });
          }}
        />

        <FinancialOverviewRow2
          monthlySummary={monthlySummary}
          supplierDebtDetails={supplierDebtDetails}
          formatVnd={formatVnd}
          onCellClick={(month, type) => onRow2CellClick?.({ month, type, companyId })}
          onSupplierDebtClick={(supplierId, supplierName) => {
            if (!supplierId || !monthParam || !companyId) {
              return;
            }

            onSupplierDebtClick?.({ supplierId, supplierName, month: monthParam, companyId });
          }}
        />

        <FinancialOverviewRow3
          row3Data={row3Data}
          row3Columns={row3Columns}
          row3GroupHeaderRows={row3GroupHeaderRows}
        />

        <FinancialOverviewRow4
          row4Data={row4Data}
          row4Columns={row4Columns}
          row4GroupHeaderRows={row4GroupHeaderRows}
        />
      </Grid>
    </Box>
    </>
  );
};

export default FinanceOverview;
